import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { ICliente, IClienteTable, IIPPermitido } from "../../models"
import { IFormError, IFormProps } from "../utils"
import { validateIp } from "../../../utils"

interface IInsertIPPermitidoFormFields {
    IdClienteAInserir: number
    IP: string
}

interface IUpdateIPPermitidoFormFields {
    IdIPPermitido: number
    IP: string
    Ativo: boolean
}

const insertIPPermitidoFormFieldsInitialState: IInsertIPPermitidoFormFields = {
    IdClienteAInserir: 0,
    IP: ""
}

const updateIPPermitidoFormFieldsInitialState: IUpdateIPPermitidoFormFields = {
    IdIPPermitido: 0,
    Ativo: false,
    IP: ''
}

interface IIPPermitidoState {
    ipPermitidoList: IIPPermitido[]
    ipPermitidoTotal: number
    clientes: IClienteTable[]
    ipPermitidoPagination: {
        page: number
        limit: number
    }
    clienteSelecionado?: ICliente
    insertIPPermitidoForm: IFormProps<IInsertIPPermitidoFormFields>
    insertIPPermitidoOpenForm: boolean
    updateIPPermitidoForm: IFormProps<IUpdateIPPermitidoFormFields>
    updateIPPermitidoOpenForm: boolean
}

const initialState: IIPPermitidoState = {
    ipPermitidoList: [],
    ipPermitidoTotal: 0,
    clientes: [],
    ipPermitidoPagination: {
        page: 1,
        limit: 5
    },
    insertIPPermitidoForm: {
        errors: [],
        fields: insertIPPermitidoFormFieldsInitialState,
        touched: false
    },
    insertIPPermitidoOpenForm: false,
    updateIPPermitidoForm: {
        errors: [],
        fields: updateIPPermitidoFormFieldsInitialState,
        touched: false
    },
    updateIPPermitidoOpenForm: false
}

export const validateInsertIPPermitidoForm = (fields: IInsertIPPermitidoFormFields): IFormError<keyof IInsertIPPermitidoFormFields>[] => {
    const errors: IFormError<keyof IInsertIPPermitidoFormFields>[] = []

    if (!fields.IdClienteAInserir || fields.IdClienteAInserir === 0) {
        errors.push({
            key: 'IdClienteAInserir',
            message: 'Selecione um cliente'
        })
    } else if (!fields.IP || fields.IP === '') {
        errors.push({
            key: 'IP',
            message: 'Digite um IP válido'
        })
    } else if (!validateIp(fields.IP)) {
        errors.push({
            key: "IP",
            message: "Digite um IP válido (ex: 192.168.0.1)"
        })
    }

    return errors
}

export const validateUpdateIPPermitidoForm = (fields: IUpdateIPPermitidoFormFields): IFormError<keyof IUpdateIPPermitidoFormFields>[] => {
    const errors: IFormError<keyof IUpdateIPPermitidoFormFields>[] = []

    if (!fields.IdIPPermitido || fields.IdIPPermitido === 0) {
        errors.push({
            key: 'IdIPPermitido',
            message: 'Selecione um IP antes de atualizar'
        })
    } else if (!validateIp(fields.IP)) {
        errors.push({
            key: "IP",
            message: "Digite um IP válido (ex: 192.168.0.1)"
        })
    }

    return errors
}

const ipPermitidoSlice = createSlice({
    initialState,
    name: 'IPPermitido',
    reducers: {
        setIPPermitidoClientesState(state, action: PayloadAction<{
            clientes: IClienteTable[]
        }>) {
            state.clientes = action.payload.clientes
        },
        resetIPPermitidoClientesState(state) {
            state.clientes = initialState.clientes
        },
        setIPPermitidoPaginationState(state, action: PayloadAction<{
            page: number
            limit: number
        }>) {
            state.ipPermitidoPagination.page = action.payload.page
            state.ipPermitidoPagination.limit = action.payload.limit
        },
        setIPPermitidoPageState(state, action: PayloadAction<number>) {
            state.ipPermitidoPagination.page = action.payload
        },
        setIPPermitidoLimitState(state, action: PayloadAction<number>) {
            state.ipPermitidoPagination.limit = action.payload
        },
        resetIPPermitidoPaginationState(state) {
            state.ipPermitidoPagination = initialState.ipPermitidoPagination
        },
        // Insert Form
        setInsertIPPermitidoFormIdClienteAInserirState(state, action: PayloadAction<number>) {
            state.insertIPPermitidoForm.fields.IdClienteAInserir = action.payload
        },
        setInsertIPPermitidoFormIPState(state, action: PayloadAction<string>) {
            state.insertIPPermitidoForm.fields.IP = action.payload
        },
        setInsertIPPermitidoFormTouchedState(state, action: PayloadAction<boolean>) {
            state.insertIPPermitidoForm.touched = action.payload
        },
        setInsertIPPermitidoFormErrorsState(state, action: PayloadAction<IFormError<keyof IInsertIPPermitidoFormFields>[]>) {
            state.insertIPPermitidoForm.errors = action.payload
        },
        resetInsertIPPermitidoFormErrorsState(state) {
            state.insertIPPermitidoForm.errors = initialState.insertIPPermitidoForm.errors
        },
        setInsertIPPermitidoOpenFormState(state, action: PayloadAction<boolean>) {
            state.insertIPPermitidoOpenForm = action.payload
        },
        resetInsertIPPermitidoFormState(state) {
            state.insertIPPermitidoForm = initialState.insertIPPermitidoForm
        },
        // Update Form
        setUpdateIPPermitidoFormIdIPPermitidoState(state, action: PayloadAction<number>) {
            state.updateIPPermitidoForm.fields.IdIPPermitido = action.payload
        },
        setUpdateIPPermitidoFormIPState(state, action: PayloadAction<string>) {
            state.updateIPPermitidoForm.fields.IP = action.payload
        },
        setUpdateIPPermitidoFormAtivoState(state, action: PayloadAction<boolean>) {
            state.updateIPPermitidoForm.fields.Ativo = action.payload
        },
        setUpdateIPPermitidoFormTouchedState(state, action: PayloadAction<boolean>) {
            state.updateIPPermitidoForm.touched = action.payload
        },
        setUpdateIPPermitidoFormErrorsState(state, action: PayloadAction<IFormError<keyof IUpdateIPPermitidoFormFields>[]>) {
            state.updateIPPermitidoForm.errors = action.payload
        },
        resetUpdateIPPermitidoFormErrorsState(state) {
            state.updateIPPermitidoForm.errors = initialState.updateIPPermitidoForm.errors
        },
        setUpdateIPPermitidoOpenFormState(state, action: PayloadAction<boolean>) {
            state.updateIPPermitidoOpenForm = action.payload
        },
        resetUpdateIPPermitidoFormState(state) {
            state.updateIPPermitidoForm = initialState.updateIPPermitidoForm
        },
        setIPPermitidoState(state, action: PayloadAction<IIPPermitido[]>) {
            state.ipPermitidoList = action.payload
        },
        resetIPPermitidoState(state) {
            state.ipPermitidoList = initialState.ipPermitidoList
        },
        setIPPermitidoTotalState(state, action: PayloadAction<number>) {
            state.ipPermitidoTotal = action.payload
        },
        resetIPPermitidoTotalState(state) {
            state.ipPermitidoTotal = initialState.ipPermitidoTotal
        },
        setClienteSelecionadoState(state, action: PayloadAction<ICliente>) {
            state.clienteSelecionado = action.payload
        },
        resetClienteSelecionadoState(state) {
            state.clienteSelecionado = initialState.clienteSelecionado
        },
    }
})

export const {
    resetClienteSelecionadoState,
    resetIPPermitidoClientesState,
    resetIPPermitidoState,
    resetIPPermitidoTotalState,
    resetIPPermitidoPaginationState,
    resetInsertIPPermitidoFormErrorsState,
    resetUpdateIPPermitidoFormErrorsState,
    resetInsertIPPermitidoFormState,
    resetUpdateIPPermitidoFormState,
    setClienteSelecionadoState,
    setIPPermitidoClientesState,
    setIPPermitidoState,
    setIPPermitidoTotalState,
    setIPPermitidoLimitState,
    setIPPermitidoPageState,
    setIPPermitidoPaginationState,
    setInsertIPPermitidoFormErrorsState,
    setInsertIPPermitidoFormIPState,
    setInsertIPPermitidoFormIdClienteAInserirState,
    setInsertIPPermitidoFormTouchedState,
    setInsertIPPermitidoOpenFormState,
    setUpdateIPPermitidoFormAtivoState,
    setUpdateIPPermitidoFormErrorsState,
    setUpdateIPPermitidoFormIPState,
    setUpdateIPPermitidoFormIdIPPermitidoState,
    setUpdateIPPermitidoFormTouchedState,
    setUpdateIPPermitidoOpenFormState
} = ipPermitidoSlice.actions

export const ipPermitidoReducer = ipPermitidoSlice.reducer